import React from 'react'
import { COMPANY_PROFILE } from '@/config/companyProfile'
import { formatDateFull } from '@/lib/dateFormat'
import { Printer, X } from 'lucide-react'

export interface PrintDocumentLine {
  material_name: string
  attributes_desc?: string
  uom: string
  qty: number
  note?: string
}

export interface PrintDocumentData {
  type: 'ISSUE' | 'INBOUND' | 'RECEIPT'
  code: string
  date: string
  created_by: string
  status: 'ACTIVE' | 'VOID' | string
  void_reason?: string | null
  edit_count?: number
  // Issue specific
  receiver_name?: string | null
  receiver_department?: string | null
  purpose?: string | null
  order_info?: string | null
  // Inbound / Receipt specific
  deliverer_name?: string | null
  supplier_name?: string | null
  po_code?: string | null
  source_desc?: string | null
  note?: string | null
  lines: PrintDocumentLine[]
}

export interface WarehouseDocumentPrintModalProps {
  isOpen: boolean
  onClose: () => void
  data: PrintDocumentData | null
  onPrint?: () => void
}

function fmtN(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === '') return '0'
  const num = typeof n === 'number' ? n : Number(n)
  if (!Number.isFinite(num)) return '0'
  return num.toLocaleString('vi-VN', { maximumFractionDigits: 2 })
}

export function WarehouseDocumentPrintModal({
  isOpen,
  onClose,
  data,
  onPrint,
}: WarehouseDocumentPrintModalProps): React.ReactElement | null {
  if (!isOpen || !data) return null

  const isIssue = data.type === 'ISSUE'
  const isVoid = data.status === 'VOID'
  const title = isIssue ? 'PHIẾU XUẤT KHO' : 'PHIẾU NHẬP KHO'

  // Extract day, month, year from data.date
  let dayStr = '……'
  let monthStr = '……'
  let yearStr = '20……'
  if (data.date) {
    const d = new Date(data.date)
    if (!Number.isNaN(d.getTime())) {
      dayStr = String(d.getDate()).padStart(2, '0')
      monthStr = String(d.getMonth() + 1).padStart(2, '0')
      yearStr = String(d.getFullYear())
    }
  }

  const handlePrint = () => {
    try {
      onPrint?.()
    } catch {
      // logging failure must never block printing
    }
    window.print()
  }

  // Minimum 12 rows for standard paper layout
  const fillerCount = Math.max(0, 12 - data.lines.length)
  const totalQty = data.lines.reduce((s, l) => s + (Number(l.qty) || 0), 0)

  return (
    <div
      className="warehouse-print-modal-backdrop"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px 10px',
        overflowY: 'auto',
      }}
    >
      <style>{`
        @page {
          size: A4 portrait;
          margin: 10mm 12mm 10mm 12mm;
          @bottom-center {
            content: "Trang " counter(page) " / " counter(pages);
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            font-size: 9pt;
            color: #555;
          }
        }
        @media print {
          body {
            background: #fff !important;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          aside, header, nav, .no-print {
            display: none !important;
          }
          .warehouse-print-modal-backdrop {
            position: static !important;
            background: none !important;
            padding: 0 !important;
            overflow: visible !important;
            display: block !important;
          }
          .warehouse-print-sheet {
            box-shadow: none !important;
            border: none !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          thead {
            display: table-header-group !important;
          }
          tr {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
      `}</style>

      {/* Action Bar (hidden when printing) */}
      <div
        className="no-print"
        style={{
          width: 'min(210mm, 96vw)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          color: '#fff',
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600 }}>
          Xem trước bản in (Khổ A4 dọc) · {title} {data.code}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handlePrint}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: '#1f6feb',
              color: '#fff',
              border: 'none',
              padding: '6px 16px',
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 3,
              cursor: 'pointer',
            }}
          >
            <Printer size={14} aria-hidden="true" /> In phiếu (Ctrl+P)
          </button>
          <button
            onClick={onClose}
            style={{
              background: '#fff',
              color: '#333',
              border: '1px solid #ccc',
              padding: '6px 14px',
              fontSize: 13,
              borderRadius: 3,
              cursor: 'pointer',
            }}
          >
            <X size={14} aria-hidden="true" /> Đóng
          </button>
        </div>
      </div>

      {/* A4 Sheet Container */}
      <div
        className="warehouse-print-sheet"
        style={{
          position: 'relative',
          width: 'min(210mm, 96vw)',
          minHeight: '297mm',
          backgroundColor: '#fff',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          padding: '14mm 15mm 14mm 15mm',
          boxSizing: 'border-box',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          color: '#111',
          fontSize: 13,
          lineHeight: 1.45,
        }}
      >
        {/* Void Watermark if cancelled */}
        {isVoid && (
          <div
            style={{
              position: 'absolute',
              top: '40%',
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(-30deg)',
              fontSize: 76,
              fontWeight: 800,
              color: 'rgba(220, 38, 38, 0.22)',
              border: '6px dashed rgba(220, 38, 38, 0.3)',
              padding: '8px 48px',
              borderRadius: 12,
              letterSpacing: 10,
              pointerEvents: 'none',
              zIndex: 10,
              userSelect: 'none',
            }}
          >
            ĐÃ HỦY
          </div>
        )}

        {/* Header: Company info & Document Code */}
        <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #111', paddingBottom: 10, marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 68 }}>
              <svg width="44" height="40" fill="none" viewBox="0 0 100 90" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 65L42 20L53 43L37 65H22Z" fill="#788694" />
                <path d="M50 18L72 65H57L46 43L50 18Z" fill="#7eb338" />
                <path d="M60 47L70 65H85L68 33L60 47Z" fill="#9ecf43" />
              </svg>
              <div style={{ fontSize: 10, fontWeight: 800, color: '#6ca434', letterSpacing: '0.05em' }}>MOCPHAT</div>
              <div style={{ fontSize: 7, color: '#666', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Furniture</div>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase' }}>
                {COMPANY_PROFILE.legalName}
              </div>
              <div style={{ fontSize: 11, color: '#333' }}>{COMPANY_PROFILE.address}</div>
              <div style={{ fontSize: 11, color: '#333' }}>
                Điện thoại: {COMPANY_PROFILE.phone} · Email: {COMPANY_PROFILE.email}
              </div>
            </div>
          </div>

          <div style={{ textAlign: 'right', minWidth: 150 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {/* monospace: mã phiếu có chữ số/dấu gạch cần căn đều để dễ đối chiếu khi ký */}
              Số: <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{data.code}</span>
            </div>
            <div style={{ fontSize: 11, color: '#444', marginTop: 2 }}>
              Ngày lập: {data.date ? formatDateFull(data.date) : '……/……/20……'}
            </div>
            {isVoid && (
              <div style={{ color: '#dc2626', fontWeight: 700, fontSize: 11, marginTop: 4 }}>
                [PHIẾU ĐÃ HỦY]
              </div>
            )}
          </div>
        </div>

        {/* Title */}
        <div style={{ textAlign: 'center', margin: '14px 0 16px 0' }}>
          <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '0.05em' }}>
            {title}
          </div>
          {isVoid && data.void_reason && (
            <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4, fontStyle: 'italic' }}>
              Lý do hủy: {data.void_reason}
            </div>
          )}
        </div>

        {/* Metadata info block */}
        <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12.5 }}>
          {isIssue ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 12 }}>
                <div>
                  <span style={{ fontWeight: 600 }}>Họ tên người nhận hàng:</span>{' '}
                  <span>{data.receiver_name || '……………………………………………………'}</span>
                </div>
                <div>
                  <span style={{ fontWeight: 600 }}>Bộ phận:</span>{' '}
                  <span>{data.receiver_department || '………………………………'}</span>
                </div>
              </div>

              <div>
                <span style={{ fontWeight: 600 }}>Lý do xuất kho:</span>{' '}
                <span>{data.purpose || '……………………………………………………………………………………'}</span>
              </div>

              {data.order_info && (
                <div>
                  <span style={{ fontWeight: 600 }}>Theo lệnh sản xuất / Đơn hàng:</span>{' '}
                  <span style={{ fontWeight: 500 }}>{data.order_info}</span>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 12 }}>
                <div>
                  <span style={{ fontWeight: 600 }}>Xuất tại kho:</span>{' '}
                  <span>Kho vật tư Mộc Phát</span>
                </div>
                {data.note && (
                  <div>
                    <span style={{ fontWeight: 600 }}>Ghi chú:</span> <span>{data.note}</span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 12 }}>
                <div>
                  <span style={{ fontWeight: 600 }}>Họ tên người giao hàng:</span>{' '}
                  <span>{data.deliverer_name || '……………………………………………………'}</span>
                </div>
                <div>
                  <span style={{ fontWeight: 600 }}>Nhà cung cấp / Nguồn:</span>{' '}
                  <span>{data.supplier_name || data.source_desc || '………………………………'}</span>
                </div>
              </div>

              {data.po_code && (
                <div>
                  <span style={{ fontWeight: 600 }}>Theo đơn đặt mua (PO):</span>{' '}
                  <span style={{ fontWeight: 500 }}>{data.po_code}</span>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 12 }}>
                <div>
                  <span style={{ fontWeight: 600 }}>Nhập tại kho:</span>{' '}
                  <span>Kho vật tư Mộc Phát</span>
                </div>
                <div>
                  <span style={{ fontWeight: 600 }}>Lý do / Nguồn nhập:</span>{' '}
                  <span>{data.note || data.source_desc || '—'}</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Lines Table */}
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginBottom: 16,
            fontSize: 12,
          }}
        >
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              <th style={{ border: '1px solid #333', padding: '6px 4px', width: 38, textAlign: 'center' }}>
                STT
              </th>
              <th style={{ border: '1px solid #333', padding: '6px 8px', textAlign: 'left' }}>
                Tên vật tư và thông số
              </th>
              <th style={{ border: '1px solid #333', padding: '6px 4px', width: 65, textAlign: 'center' }}>
                ĐVT
              </th>
              <th style={{ border: '1px solid #333', padding: '6px 8px', width: 120, textAlign: 'right' }}>
                {isIssue ? 'Số lượng thực xuất' : 'Số lượng thực nhập'}
              </th>
              <th style={{ border: '1px solid #333', padding: '6px 8px', width: 140, textAlign: 'left' }}>
                Ghi chú
              </th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((l, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #333', padding: '5px 4px', textAlign: 'center' }}>
                  {idx + 1}
                </td>
                <td style={{ border: '1px solid #333', padding: '5px 8px' }}>
                  <div style={{ fontWeight: 600 }}>{l.material_name}</div>
                  {l.attributes_desc && (
                    <div style={{ fontSize: 11, color: '#555', marginTop: 1 }}>{l.attributes_desc}</div>
                  )}
                </td>
                <td style={{ border: '1px solid #333', padding: '5px 4px', textAlign: 'center' }}>
                  {l.uom || '—'}
                </td>
                <td style={{ border: '1px solid #333', padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>
                  {fmtN(l.qty)}
                </td>
                <td style={{ border: '1px solid #333', padding: '5px 8px', color: '#555' }}>
                  {l.note || ''}
                </td>
              </tr>
            ))}

            {/* Filler blank rows to guarantee clean sheet structure for >= 12 rows */}
            {Array.from({ length: fillerCount }).map((_, fIdx) => (
              <tr key={`fill-${fIdx}`} style={{ height: 26 }}>
                <td style={{ border: '1px solid #333', padding: '5px 4px', textAlign: 'center', color: '#aaa' }}>
                  {data.lines.length + fIdx + 1}
                </td>
                <td style={{ border: '1px solid #333', padding: '5px 8px' }}></td>
                <td style={{ border: '1px solid #333', padding: '5px 4px' }}></td>
                <td style={{ border: '1px solid #333', padding: '5px 8px' }}></td>
                <td style={{ border: '1px solid #333', padding: '5px 8px' }}></td>
              </tr>
            ))}

            {/* Total Row */}
            <tr style={{ background: '#fafafa', fontWeight: 700 }}>
              <td
                colSpan={3}
                style={{ border: '1px solid #333', padding: '6px 8px', textAlign: 'right' }}
              >
                Cộng: {data.lines.length} khoản
              </td>
              <td style={{ border: '1px solid #333', padding: '6px 8px', textAlign: 'right' }}>
                {fmtN(totalQty)}
              </td>
              <td style={{ border: '1px solid #333', padding: '6px 8px' }}></td>
            </tr>
          </tbody>
        </table>

        {/* Date line before signatures */}
        <div style={{ textAlign: 'right', fontStyle: 'italic', marginBottom: 12, fontSize: 12 }}>
          Ngày {dayStr} tháng {monthStr} năm {yearStr}
        </div>

        {/* 4 Signatures Block: height >= 25mm space */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr 1fr',
            textAlign: 'center',
            fontSize: 12,
            pageBreakInside: 'avoid',
          }}
        >
          {/* Column 1: Người lập phiếu */}
          <div>
            <div style={{ fontWeight: 700 }}>Người lập phiếu</div>
            <div style={{ fontSize: 11, fontStyle: 'italic', color: '#555' }}>(Ký, họ tên)</div>
            <div style={{ height: 95 }} />
            <div style={{ fontWeight: 600 }}>{data.created_by || '………………………'}</div>
          </div>

          {/* Column 2: Người nhận hàng / Người giao hàng */}
          <div>
            <div style={{ fontWeight: 700 }}>
              {isIssue ? 'Người nhận hàng' : 'Người giao hàng'}
            </div>
            <div style={{ fontSize: 11, fontStyle: 'italic', color: '#555' }}>(Ký, họ tên)</div>
            <div style={{ height: 95 }} />
            <div style={{ fontWeight: 600 }}>
              {isIssue ? (data.receiver_name || '………………………') : (data.deliverer_name || '………………………')}
            </div>
          </div>

          {/* Column 3: Thủ kho */}
          <div>
            <div style={{ fontWeight: 700 }}>Thủ kho</div>
            <div style={{ fontSize: 11, fontStyle: 'italic', color: '#555' }}>(Ký, họ tên)</div>
            <div style={{ height: 95 }} />
            <div style={{ fontWeight: 600 }}>………………………</div>
          </div>

          {/* Column 4: Ký duyệt */}
          <div>
            <div style={{ fontWeight: 700 }}>Ký duyệt</div>
            <div style={{ fontSize: 11, fontStyle: 'italic', color: '#555' }}>(Ký, họ tên)</div>
            <div style={{ height: 95 }} />
            <div style={{ fontSize: 11, color: '#555' }}>Trưởng bộ phận / Giám đốc</div>
          </div>
        </div>

        {/* Footer info (edit count) */}
        {data.edit_count != null && data.edit_count > 0 && (
          <div
            style={{
              marginTop: 20,
              fontSize: 11,
              color: '#666',
              fontStyle: 'italic',
              borderTop: '1px dashed #ccc',
              paddingTop: 4,
            }}
          >
            {`Đã chỉnh sửa ${data.edit_count} lần`}
          </div>
        )}
      </div>
    </div>
  )
}
